import { api } from './client';

export type BackendSupplyItemStatus = 'SUFFICIENT' | 'LOW_STOCK' | 'OUT_OF_STOCK' | 'DISCONTINUED';

export interface BackendSupplyItem {
  _id: string;
  htxId: string;
  code: string;
  name: string;
  group?: string;
  stock: number;
  reserved: number;
  available: number;
  unit: string;
  purchasePriceVnd?: number;
  supplyPriceVnd?: number;
  supplier?: string;
  supplierId?: string;
  batchNo?: string;
  expiryDate?: string;
  status: BackendSupplyItemStatus;
  lowStockThreshold: number;
  notes?: string;
}

export type BackendMaterialOrigin = 'HTX_ISSUED' | 'SELF_PURCHASED';

export type BackendMaterialStatus = 'PENDING_ISSUE' | 'ISSUED' | 'IN_USE' | 'RETURNED' | 'DEPLETED';

export interface BackendMaterialAttachment {
  fileId: string;
  type: 'image' | 'video' | 'audio' | 'doc';
  caption?: string;
}

export interface BackendMaterial {
  _id: string;
  farmerId: string;
  seasonId?: string;
  loanId?: string;
  supplyItemId?: string;
  code?: string;
  name: string;
  group?: string;
  quantity: number;
  remainingQuantity?: number;
  unit: string;
  origin: BackendMaterialOrigin;
  issuedDate: string;
  returnedDate?: string;
  status: BackendMaterialStatus;
  notes?: string;
  attachments?: BackendMaterialAttachment[];
}

export type BackendSupplyTransactionType = 'IN' | 'OUT' | 'ADJUST';

export interface BackendSupplyTransaction {
  _id: string;
  supplyItemId: string;
  farmerId?: string;
  materialId?: string;
  type: BackendSupplyTransactionType;
  quantity: number;
  stockBefore: number;
  stockAfter: number;
  reason: string;
  performedBy: string;
  createdAt: string;
}

export interface CreateSupplyItemPayload {
  htxId: string;
  code: string;
  name: string;
  group?: string;
  initialStock?: number;
  unit: string;
  purchasePriceVnd?: number;
  supplyPriceVnd?: number;
  supplier?: string;
  supplierId?: string;
  batchNo?: string;
  expiryDate?: string;
  lowStockThreshold?: number;
  notes?: string;
}

export async function createSupplyItem(data: CreateSupplyItemPayload): Promise<BackendSupplyItem> {
  return api.post<BackendSupplyItem>('/supply-items', data);
}

/** Descriptive fields only — stock moves exclusively through stock-in/stock-out/
 * adjust so every quantity change lands in the transaction ledger with a reason. */
export interface UpdateSupplyItemPayload {
  code?: string;
  name?: string;
  group?: string;
  unit?: string;
  purchasePriceVnd?: number;
  supplyPriceVnd?: number;
  supplier?: string;
  supplierId?: string;
  batchNo?: string;
  expiryDate?: string;
  lowStockThreshold?: number;
  notes?: string;
}

export async function updateSupplyItem(id: string, data: UpdateSupplyItemPayload): Promise<BackendSupplyItem> {
  return api.patch<BackendSupplyItem>(`/supply-items/${id}`, data);
}

export async function deleteSupplyItem(id: string): Promise<void> {
  await api.delete<{ deleted: true }>(`/supply-items/${id}`);
}

export async function stockInSupplyItem(id: string, quantity: number, reason: string): Promise<BackendSupplyItem> {
  return api.post<BackendSupplyItem>(`/supply-items/${id}/stock-in`, { quantity, reason });
}

export async function stockOutSupplyItem(id: string, quantity: number, reason: string): Promise<BackendSupplyItem> {
  return api.post<BackendSupplyItem>(`/supply-items/${id}/stock-out`, { quantity, reason });
}

export async function adjustSupplyItem(id: string, newStock: number, reason: string): Promise<BackendSupplyItem> {
  return api.post<BackendSupplyItem>(`/supply-items/${id}/adjust`, { newStock, reason });
}

export async function getSupplyItemById(id: string): Promise<BackendSupplyItem> {
  return api.get<BackendSupplyItem>(`/supply-items/${id}`);
}

export async function getSupplyItemHistory(id: string): Promise<BackendSupplyTransaction[]> {
  return api.get<BackendSupplyTransaction[]>(`/supply-items/${id}/history`);
}

export async function listSupplyItemsByHtx(htxId: string): Promise<BackendSupplyItem[]> {
  return api.get<BackendSupplyItem[]>(`/supply-items/htx/${htxId}`);
}

export interface IssueMaterialPayload {
  supplyItemId: string;
  farmerId: string;
  quantity: number;
  seasonId?: string;
  loanId?: string;
  notes?: string;
}

export async function issueMaterialToFarmer(data: IssueMaterialPayload): Promise<BackendMaterial> {
  return api.post<BackendMaterial>('/materials/issue', data);
}

export interface CreateSelfPurchasedMaterialPayload {
  farmerId: string;
  seasonId?: string;
  name: string;
  group?: string;
  quantity: number;
  unit: string;
  notes?: string;
  attachments?: BackendMaterialAttachment[];
}

export async function createSelfPurchasedMaterial(data: CreateSelfPurchasedMaterialPayload): Promise<BackendMaterial> {
  return api.post<BackendMaterial>('/materials/self-purchased', data);
}

export async function listMaterialsByFarmer(farmerId: string, seasonId?: string): Promise<BackendMaterial[]> {
  const query = seasonId ? `?seasonId=${encodeURIComponent(seasonId)}` : '';
  return api.get<BackendMaterial[]>(`/materials/farmers/${farmerId}${query}`);
}

export async function updateMaterialStatus(id: string, status: BackendMaterialStatus): Promise<BackendMaterial> {
  return api.patch<BackendMaterial>(`/materials/${id}/status`, { status });
}

/** Only a SELF_PURCHASED material can go — the backend rejects HTX-issued or
 * loan-linked ones, since those mirror entries already in the supply ledger. */
export async function deleteMaterial(id: string): Promise<void> {
  await api.delete<{ deleted: true }>(`/materials/${id}`);
}
